/* ------------------------------------------------------------------ */
/*  Regex-based XML parser for Wazuh <decoder> and <rule> blocks      */
/* ------------------------------------------------------------------ */

import type { WazuhDecoder, WazuhRule, MitreInfo, ParseMessage } from './types';

/** Extract the text content of an XML child element */
function childText(xml: string, tag: string): string | undefined {
  const re = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, 'i');
  const m = xml.match(re);
  return m ? m[1].trim() : undefined;
}

/** Extract an attribute value from an opening tag */
function attr(tag: string, name: string): string | undefined {
  const re = new RegExp(`${name}\\s*=\\s*"([^"]*)"`, 'i');
  const m = tag.match(re);
  return m ? m[1] : undefined;
}

function collectTagNames(xml: string): Set<string> {
  const names = new Set<string>();
  const re = /<\s*\/?\s*([a-zA-Z_][\w:-]*)\b/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(xml)) !== null) {
    names.add(match[1].toLowerCase());
  }
  return names;
}

function parseIntField(
  value: string,
  fieldName: string,
  messages: ParseMessage[],
  context: string,
  { min }: { min?: number } = {}
): number | null {
  const parsed = Number.parseInt(value.trim(), 10);
  if (Number.isNaN(parsed)) {
    messages.push({
      type: 'error',
      message: `${context}: <${fieldName}> must be a number.`,
    });
    return null;
  }

  if (min !== undefined && parsed < min) {
    messages.push({
      type: 'error',
      message: `${context}: <${fieldName}> must be >= ${min}.`,
    });
    return null;
  }

  return parsed;
}

/* ------------------------------------------------------------------ */
/*  Decoder parsing                                                   */
/* ------------------------------------------------------------------ */

export function parseDecoders(xml: string): {
  decoders: WazuhDecoder[];
  messages: ParseMessage[];
} {
  const decoders: WazuhDecoder[] = [];
  const messages: ParseMessage[] = [];
  const allowedDecoderTags = new Set(['program_name', 'prematch', 'regex', 'order']);

  const blockRe = /<decoder\s+name\s*=\s*"([^"]+)"[^>]*>([\s\S]*?)<\/decoder>/gi;
  let match: RegExpExecArray | null;

  while ((match = blockRe.exec(xml)) !== null) {
    const name = match[1];
    const body = match[2];

    const decoder: WazuhDecoder = { name };

    const pn = childText(body, 'program_name');
    if (pn) decoder.program_name = pn;

    const pm = childText(body, 'prematch');
    if (pm) decoder.prematch = pm;

    const rx = childText(body, 'regex');
    if (rx) decoder.regex = rx;

    const order = childText(body, 'order');
    if (order) {
      decoder.order = order.split(',').map((s) => s.trim());
    }

    const tagNames = collectTagNames(body);
    const unknownTags = Array.from(tagNames).filter(
      (tag) => !allowedDecoderTags.has(tag)
    );
    if (unknownTags.length > 0) {
      messages.push({
        type: 'warning',
        message: `Decoder "${name}": unsupported element(s): ${unknownTags.join(', ')}.`,
      });
    }

    // Validate: regex without order is a warning
    if (decoder.regex && !decoder.order) {
      messages.push({
        type: 'warning',
        message: `Decoder "${name}": <regex> without <order> — fields won't be extracted.`,
      });
    }

    decoders.push(decoder);
  }

  if (decoders.length === 0 && xml.trim().length > 0) {
    messages.push({
      type: 'warning',
      message:
        'No <decoder> blocks found. Wrap decoders in <decoder name="...">...</decoder>.',
    });
  }

  return { decoders, messages };
}

/* ------------------------------------------------------------------ */
/*  Rule parsing                                                      */
/* ------------------------------------------------------------------ */

export function parseRules(xml: string): {
  rules: WazuhRule[];
  messages: ParseMessage[];
} {
  const rules: WazuhRule[] = [];
  const messages: ParseMessage[] = [];
  const seenRuleIds = new Set<number>();
  const allowedRuleTags = new Set([
    'decoded_as',
    'match',
    'regex',
    'srcip',
    'program_name',
    'if_sid',
    'frequency',
    'timeframe',
    'same_source_ip',
    'description',
    'mitre',
    'id',
    'tactic',
  ]);

  const blockRe = /<rule\s+([^>]+)>([\s\S]*?)<\/rule>/gi;
  let match: RegExpExecArray | null;

  while ((match = blockRe.exec(xml)) !== null) {
    const attrs = match[1];
    const body = match[2];

    const idStr = attr(attrs, 'id');
    const levelStr = attr(attrs, 'level');

    if (!idStr || !levelStr) {
      messages.push({
        type: 'error',
        message: `Rule missing required id or level attribute.`,
      });
      continue;
    }

    const id = parseInt(idStr, 10);
    const level = parseInt(levelStr, 10);

    if (isNaN(id) || isNaN(level)) {
      messages.push({
        type: 'error',
        message: `Rule id="${idStr}" level="${levelStr}": id and level must be numbers.`,
      });
      continue;
    }

    if (seenRuleIds.has(id)) {
      messages.push({
        type: 'error',
        message: `Duplicate rule id "${id}" found. Rule IDs must be unique.`,
      });
      continue;
    }
    seenRuleIds.add(id);

    const rule: WazuhRule = { id, level };

    const da = childText(body, 'decoded_as');
    if (da) rule.decoded_as = da;

    const mt = childText(body, 'match');
    if (mt) rule.match = mt;

    const rx = childText(body, 'regex');
    if (rx) rule.regex = rx;

    const srcip = childText(body, 'srcip');
    if (srcip) rule.srcip = srcip;

    const pn = childText(body, 'program_name');
    if (pn) rule.program_name = pn;

    const ifSid = childText(body, 'if_sid');
    if (ifSid) {
      const parsed = parseIntField(ifSid, 'if_sid', messages, `Rule ${id}`, { min: 1 });
      if (parsed === null) continue;
      rule.if_sid = parsed;
    }

    const freq = childText(body, 'frequency');
    if (freq) {
      const parsed = parseIntField(freq, 'frequency', messages, `Rule ${id}`, { min: 1 });
      if (parsed === null) continue;
      rule.frequency = parsed;
    }

    const tf = childText(body, 'timeframe');
    if (tf) {
      const parsed = parseIntField(tf, 'timeframe', messages, `Rule ${id}`, { min: 1 });
      if (parsed === null) continue;
      rule.timeframe = parsed;
    }

    // same_source_ip is a self-closing tag or empty element
    if (/<same_source_ip\s*\/?>/.test(body)) {
      rule.same_source_ip = true;
    }

    const desc = childText(body, 'description');
    if (desc) rule.description = desc;

    // MITRE parsing
    const mitreBlock = body.match(/<mitre>([\s\S]*?)<\/mitre>/i);
    if (mitreBlock) {
      const mitreId = childText(mitreBlock[1], 'id');
      if (mitreId) {
        const mitre: MitreInfo = { id: mitreId };
        const tactic = childText(mitreBlock[1], 'tactic');
        if (tactic) mitre.tactic = tactic;
        rule.mitre = mitre;
      }
    }

    const tagNames = collectTagNames(body);
    const unknownTags = Array.from(tagNames).filter((tag) => !allowedRuleTags.has(tag));
    if (unknownTags.length > 0) {
      messages.push({
        type: 'warning',
        message: `Rule ${id}: unsupported element(s): ${unknownTags.join(', ')}.`,
      });
    }

    rules.push(rule);
  }

  if (rules.length === 0 && xml.trim().length > 0) {
    messages.push({
      type: 'warning',
      message:
        'No <rule> blocks found. Wrap rules in <rule id="..." level="...">...</rule>.',
    });
  }

  return { rules, messages };
}
